import {
  Card,
  Group,
  Text,
  createStyles,
  SimpleGrid,
  rem,
  getStylesRef,
  Center,
  Skeleton,
  Stack,
  MediaQuery,
} from '@mantine/core';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { IconEye, IconHeart } from '@tabler/icons';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { AdvancedImage, lazyload, accessibility, responsive, placeholder } from '@cloudinary/react';
import { name as transformationName } from '@cloudinary/url-gen/actions/namedTransformation';
import { TIER_IMAGES_CDN } from '../src/Constants';
import { Database } from '../types/supabase';
import { cld } from '../src/Utils';

export type TemplateData = Database['public']['Tables']['tier_lists']['Row'];
export type CategoryData = Database['public']['Views']['template_stats']['Row'];
export type TierListData = Database['public']['Tables']['tier_lists_save']['Row'];

function isTemplateData(data: any): data is TemplateData {
  return (
    (data as TemplateData).creator_id !== undefined &&
    (data as TemplateData).description !== undefined
  );
}
function isCategoryData(data: any): data is CategoryData {
  return (
    (data as TemplateData).creator_id !== undefined &&
    (data as CategoryData).quality_score !== undefined
  );
}

const useStyles = createStyles((theme) => ({
  searchResult: {
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    // padding: theme.spacing.xl,
    // backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[1],
    [`&:hover`]: {
      transform: 'scale(1.05)',
    },
  },
  card: {
    position: 'relative',
    aspectRatio: '1',
    width: 200,

    [theme.fn.smallerThan('sm')]: {
      width: 125,
    },
    // height: rem(280),
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],

    [`&:hover .${getStylesRef('image')}`]: {
      transform: 'scale(1.05)',
    },
  },

  image: {
    ...theme.fn.cover(),
    // ref: getStylesRef('image'),
    backgroundSize: 'cover',
    transition: 'transform 500ms ease',
    width: '100%',
    height: '100%',
  },

  overlay: {
    position: 'absolute',
    top: '20%',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, .85) 90%)',
    [theme.fn.smallerThan('md')]: {
      top: 0,
      backgroundImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, .92) 90%)',
    },
  },

  content: {
    height: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    zIndex: 1,
  },

  title: {
    color: theme.white,
    marginBottom: rem(5),

    [theme.fn.smallerThan('sm')]: {
      marginBottom: rem(2),
    },
  },

  bodyText: {
    color: theme.colors.dark[2],
    marginLeft: rem(7),
  },

  author: {
    color: theme.colors.dark[2],
  },
}));

function TierListCard({ result }: { result: TemplateData | TierListData | CategoryData }) {
  const { classes, theme } = useStyles();
  const thumbnail = `${TIER_IMAGES_CDN}/${result.thumbnail}`;
  const supabase = useSupabaseClient<Database>();
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [cardName, setCardName] = useState<string | undefined>(undefined);
  const [viewCount, setViewCount] = useState<number>(1);
  const [likeCount, setLikeCount] = useState<number>(0);
  const [templateData, setTemplateData] = useState<TemplateData | CategoryData | undefined>(
    undefined
  );
  useEffect(() => {
    async function loadUserName() {
      const { data } = await supabase
        .from('profiles_public')
        .select('username')
        .eq(
          'id',
          isTemplateData(result) || isCategoryData(result) ? result.creator_id : result.user_id
        )
        .single();
      setUsername(data?.username ?? undefined);
    }

    async function loadLikeCount() {
      const { count } = await supabase
        .from('template_likes')
        .select('*', { count: 'exact', head: true })
        .eq('template_id', (result as TemplateData).id);

      if (count != null) {
        setLikeCount(count);
      }
    }

    async function loadViewCount() {
      const { data } = await supabase
        .from(isTemplateData(result) ? 'template_views' : 'tier_list_views')
        .select('views')
        .eq('id', isTemplateData(result) || isCategoryData(result) ? result.id : result.save_id)
        .single();
      setViewCount(data?.views ?? 1);
    }
    async function loadTierListInfo() {
      if (!isTemplateData(result) && !isCategoryData(result)) {
        const { data } = await supabase
          .from('tier_lists')
          .select('*')
          .eq('id', result.template_id)
          .single();
        setTemplateData(data ?? undefined);
        setCardName(data?.name ?? undefined);
      }
    }

    if (isTemplateData(result) || isCategoryData(result)) {
      setTemplateData(result);
      setCardName(result.name ?? '');
      loadLikeCount();
    }
    loadUserName();
    loadTierListInfo();
    if (!isCategoryData(result)) {
      loadViewCount();
    } else {
      setViewCount(result.views ?? 0);
    }
  }, [result]);

  const image = cld
    .image(thumbnail)
    .setDeliveryType('fetch')
    .namedTransformation(transformationName('tier-list-image-1x'));
  const image2x = cld
    .image(thumbnail)
    .setDeliveryType('fetch')
    .namedTransformation(transformationName('tier-list-image-2x'));
  const image3x = cld
    .image(thumbnail)
    .setDeliveryType('fetch')
    .namedTransformation(transformationName('tier-list-image-3x'));

  console.log(image.toURL());
  return (
    <>
      {templateData ? (
        <Link
          href={`/${templateData.id}/${templateData.slug}${
            isTemplateData(result) || isCategoryData(result) ? '' : `/list/${result.save_id}`
          }`}
          passHref
          style={{ textDecoration: 'none' }}
        >
          <Card p="lg" shadow="lg" className={classes.card} radius="md">
            <MediaQuery smallerThan="sm" styles={{ display: 'none' }} className={classes.image}>
              <AdvancedImage
                srcset={`${image2x.toURL()} 1x, ${image3x.toURL()} 2x`}
                cldImg={image2x}
                plugins={[lazyload()]}
                class={classes.image}
              />
            </MediaQuery>
            <MediaQuery largerThan="sm" styles={{ display: 'none' }} className={classes.image}>
              <AdvancedImage
                srcset={`${image.toURL()} 1x, ${image2x.toURL()} 2x, ${image3x.toURL()} 3x`}
                cldImg={image}
                plugins={[lazyload()]}
                class={classes.image}
              />
            </MediaQuery>
            {/* <div className={classes.image} style={{ backgroundImage: `url(${thumbnail})` }} /> */}
            <div className={classes.overlay} />

            <Stack
              sx={{
                position: 'absolute',
                bottom: rem(10),
                gap: 0,
                left: 0,
                width: '100%',
              }}
              px="xs"
            >
              <MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
                <Text size="lg" className={classes.title} weight={500}>
                  {cardName}
                </Text>
              </MediaQuery>
              <MediaQuery largerThan="sm" styles={{ display: 'none' }}>
                <Text size="sm" className={classes.title} weight={500} lineClamp={3}>
                  {cardName}
                </Text>
              </MediaQuery>

              <Group position="apart" spacing="xs">
                <MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
                  {username ? (
                    <Text size="sm" className={classes.author}>
                      {username}
                    </Text>
                  ) : (
                    <Skeleton h={15} sx={{ flex: 1 }} />
                  )}
                </MediaQuery>

                <Group>
                  <Center>
                    <IconEye size="1rem" stroke={1.5} color={theme.colors.dark[2]} />
                    <Text size="sm" className={classes.bodyText}>
                      {viewCount}
                    </Text>

                    {(isTemplateData(result) || isCategoryData(result)) && (
                      <Group pl="sm" style={{ gap: 0 }}>
                        <IconHeart size="1rem" stroke={1.5} color={theme.colors.dark[2]} />
                        <Text size="sm" className={classes.bodyText} ml={3}>
                          {likeCount}
                        </Text>
                      </Group>
                    )}
                  </Center>
                </Group>
              </Group>
            </Stack>
          </Card>
        </Link>
      ) : (
        <Skeleton className={classes.card} />
      )}
    </>
  );
}

export default function TemplateResults({
  templates,
  tierLists,
  skeletonCount = 3,
  isLoading = false,
}: {
  templates: TemplateData[] | CategoryData[];
  tierLists?: Database['public']['Tables']['tier_lists_save']['Row'][] | undefined;
  skeletonCount?: number;
  isLoading?: boolean;
}) {
  const { classes } = useStyles();
  return (
    <Group
      my="lg"
      sx={{
        width: '100%',
      }}
    >
      {isLoading && (
        <>
          {[...Array(skeletonCount)].map((_, index) => (
            <Skeleton key={index} className={classes.card} />
          ))}
        </>
      )}
      {templates.map((result) => (
        <TierListCard key={result.id} result={result} />
      ))}
      {tierLists &&
        tierLists.map((tierList) => <TierListCard key={tierList.save_id} result={tierList} />)}
    </Group>
  );
}
